import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { useCallback, useMemo } from 'react';
import { apiTokenRequest } from './msalConfig';
import { RedirectInProgressError, isInteractionInProgress } from './redirectInProgress';
import { AccessDeniedError, isEntitlementRefusal, wasAccessDeniedAtRedirect } from './accessDenied';

export function useAuth() {
  // #14687 — inProgress guards against issuing a second top-level navigation.
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = accounts[0] ?? null;

  const login = useCallback(() => {
    instance.loginRedirect({ scopes: ['openid', 'profile'] });
  }, [instance]);

  const logout = useCallback(() => {
    instance.logoutRedirect({ postLogoutRedirectUri: '/' });
  }, [instance]);

  const getAccessToken = useCallback(async (): Promise<string> => {
    if (!account) throw new Error('No authenticated account');
    // #19758 — Entra already refused this account's API token on the way back from a redirect
    // (msalInstance.ts). Asking again would only redirect again: the infinite loop.
    if (wasAccessDeniedAtRedirect()) throw new AccessDeniedError();
    try {
      const response = await instance.acquireTokenSilent({
        ...apiTokenRequest,
        account,
      });
      return response.accessToken;
    } catch (error) {
      // #19758 — AADSTS50105: the account is not assigned to the API app. That is "no entitlement
      // at all", not "sign in again" — checked BEFORE the interaction-required branch, because
      // Entra reports it as interaction-required and answering it with acquireTokenRedirect is
      // exactly the loop. Every other interaction-required error keeps the redirect below.
      if (isEntitlementRefusal(error)) throw new AccessDeniedError();
      if (error instanceof InteractionRequiredAuthError) {
        // #14687 — throw rather than `return ''`: an empty string is indistinguishable from a real
        // token, so the caller fetched with an empty bearer and 401'd instead of simply letting the
        // redirect complete.
        if (isInteractionInProgress(inProgress)) throw new RedirectInProgressError();
        await instance.acquireTokenRedirect(apiTokenRequest);
        throw new RedirectInProgressError();
      }
      throw error;
    }
  }, [instance, account, inProgress]);

  const roles = useMemo<string[]>(() => {
    if (!account?.idTokenClaims) return [];
    const claims = account.idTokenClaims as Record<string, unknown>;
    const r = claims['roles'];
    return Array.isArray(r) ? r : [];
  }, [account]);

  const displayName = account?.name ?? account?.username ?? '';
  const email = account?.username ?? '';
  const entraId = account?.localAccountId ?? '';

  return {
    isAuthenticated,
    account,
    displayName,
    email,
    entraId,
    roles,
    login,
    logout,
    getAccessToken,
  };
}
